import { collection, doc, DocumentData, writeBatch, getDoc, getDocs, runTransaction, updateDoc, setDoc } from "firebase/firestore"
import { db } from "./config"
import { __DEV__, Logger, TOP_AMOUNT, TYPES } from "@lib/helpers"
import { ICategory, IStation, ISurvey, IType } from "@models"

const collectionName = __DEV__? 'stations': 'stations'

let stations: IStation[] = []
let stationsMapIndex: { [key: string]: number } = {}
let stationsCategories: { [key in ICategory]?: IStation[] } = {}

const toStation = (station: DocumentData) => {
  const name = station.name.main || station.name
  station.name = name
  station.freq = station.freq || ''
  return station as IStation
}

const sortStations = (stations: IStation[]) => 
  stations.sort((a, b) => {
    const orderA = a.order || TYPES[a.type].order || 999
    const orderB = b.order || TYPES[b.type].order || 999
    return orderA - orderB
})
export const fetchStationsCategories = async () => {
  if (stations.length > 0) return stationsCategories
  await fetchStations()
  return stationsCategories
}
export const fetchCategoryStations = async (category: ICategory) => {
  const allStations = stations.length > 0? stations: await fetchStations()
  if (category === "top") return allStations.slice(0, 25)
  return allStations.filter(station => [...station.categories, station.type].includes(category))
}
export const fetchTypeStations = async (type: IType) => {
  const allStations = stations.length > 0? stations: await fetchStations()
  return allStations.filter(station => station.type === type)
}
export const fetchStationsForce = async () => {
  stations = []
  return await fetchStations()
}
const initStations = async (dbStations: IStation[]) => {
  stations = sortStations(dbStations)
  stationsMapIndex = stations.reduce((acc, station, index) => {
    acc[station.id] = index
    return acc
  }, {} as { [key: string]: number })
  stationsCategories = {
    top: stations.slice(0, TOP_AMOUNT),
    favorites: []
  }
  stations.forEach(station => {
    for (const category of (station.categories)) {
      if (!stationsCategories[category])
        stationsCategories[category] = []
      stationsCategories[category].push(station)
    }
  })
}
export const fetchStations = async () => {
  if (stations.length > 0) return stations
  const stationsCollection = collection(db, collectionName)
  const stationsSnapshot = await getDocs(stationsCollection)
  const dbStations = stationsSnapshot.docs.map(doc => toStation(doc.data()))
  initStations(dbStations)
  return stations
}
export const fetchStationsOld = async () => {
  const stationsCollection = collection(db, collectionName)
  const stationsSnapshot = await getDocs(stationsCollection)
  return stationsSnapshot.docs.map(doc => doc.data())
}
export const fetchStation = async (id: string) => {
  if (stations.length > 0)
    return stations[stationsMapIndex[id]]
  const stationRef = doc(db, collectionName, id)
  const stationSnapshot = await getDoc(stationRef)
  return stationSnapshot.exists()? toStation(stationSnapshot.data()): undefined
}
export const updateStationsOrder = async (stations: { id: string, order: number }[]) => {
  const batch = writeBatch(db)
  stations.forEach((station) => {
    const stationRef = doc(db, collectionName, station.id)
    batch.update(stationRef, { order: station.order })
  })
  try {
    await batch.commit()
    console.info('Stations order updated successfully')
  } catch (error) {
    console.error('Error updating stations order:', error)
  }
}
export const updateStation = async (station: IStation) => {
  const stationRef = doc(db, collectionName, station.id)
  const newStation = { ...station }
  if (!newStation.freq) delete newStation.freq
  if (!newStation.freqs) delete newStation.freqs

  try {
    const docSnapshot = await getDoc(stationRef)

    if (docSnapshot.exists()) {
      // Update the station if it exists
      await updateDoc(stationRef, newStation)
      stations[stationsMapIndex[station.id]] = station
      console.info(`Station ${station.id} updated successfully`)
    } else {
      // Add a new station if it doesn't exist
      await setDoc(stationRef, newStation)
      stations.push(station) // Add the new station to the local cache
      stationsMapIndex[station.id] = stations.length - 1
      console.info(`Station ${station.id} added successfully`)
    }
  } catch (error) {
    console.error(`Error updating station ${station.id}:`, error)
  }
}
export const updateStationField = async (id: string, field: string, value: any) => {
  const stationRef = doc(db, collectionName, id)
  try {
    await updateDoc(stationRef, { [field]: value })
    console.info(`Station ${id} updated successfully`)
  } catch (error) {
    console.error(`Error updating station ${id}:`, error)
  }
}
export const updateRating = async (id: string, rating: number, isNew: boolean) => {
  const stationRef = doc(db, collectionName, id)
  const newVotes = await runTransaction(db, async transaction => {
    const stationSnapshot = await transaction.get(stationRef)
    if (!stationSnapshot.exists()) return
    const stationData = stationSnapshot.data() as IStation
    const _newVotes = {
      total: stationData.votes.total + rating,
      count: stationData.votes.count + (isNew? 1: 0)
    }
    transaction.update(stationRef, { votes: _newVotes })
    return _newVotes
  })
  if (!newVotes) return
  await fetchStations()
  stations[stationsMapIndex[id]].votes = newVotes
  return newVotes
}
export const setSurvey = async (survey: ISurvey) => {
  const surveyRef = doc(db, 'surveys', survey.id)
  try {
    await setDoc(surveyRef, survey)
    console.info(`Survey listening added successfully`)
  } catch (error) {
    console.error(`Error adding survey listening:`, error)
  }
}
export const fetchSurveys = async () => {
  const surveysCollection = collection(db, 'surveys')
  const surveysSnapshot = await getDocs(surveysCollection)
  return surveysSnapshot.docs.map(doc => doc.data())
}
export const fetchSurvey = async (id: string) => {
  const surveyRef = doc(db, 'surveys', id)
  const surveySnapshot = await getDoc(surveyRef)
  return surveySnapshot.exists()? surveySnapshot.data() as ISurvey: undefined
}
export const voteSurvey = async (id: string, newVote: number, oldVote: number) => {
  const surveyRef = doc(db, 'surveys', id)
  const newSurvey = await runTransaction(db, async transaction => {
    const surveySnapshot = await transaction.get(surveyRef)
    if (!surveySnapshot.exists()) return
    const surveyData = surveySnapshot.data() as ISurvey
    surveyData.answers[newVote].votes++
    if (oldVote > -1)
      surveyData.answers[oldVote].votes--
    transaction.update(surveyRef, {...surveyData})
    return surveyData
  })
  if (!newSurvey) return
  return newSurvey
}
